import React, { useState, useMemo, useEffect } from 'react';
import { Control, useController } from 'react-hook-form';
import { Card, CardContent } from '@design-system/Card';
import { Input } from '@/components/design-system/Input';
import { Textarea } from '@/components/design-system/Textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/design-system/Select';
import { cn } from '@/utils/styles';
import {
  Range,
  Validation,
} from '@viome/viome-base/src/usecase/Questionnaire/Questionnaire.model';

interface Props {
  control: Control<Record<string, FieldValue | string>>;
  name: string;
  title: string;
  subtitle?: string;
  placeholder?: string;
  required?: boolean;
  ranges?: Range[] | number[] | Validation;
  disabled?: boolean;
  type?: 'text' | 'number';
  multiline?: boolean;
}

interface NormalizedRange {
  min: number;
  max: number;
  unit?: string;
  precision?: number;
  default?: number;
}

interface FieldValue {
  value: number | string;
  unit?: string;
}

const isRange = (value: unknown): value is Range => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'min' in value &&
    'max' in value
  );
};

const isFieldValue = (value: unknown): value is FieldValue => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'value' in value &&
    (typeof value.value === 'number' || typeof value.value === 'string')
  );
};

const InputFieldWrapper: React.FC<Props> = ({
  control,
  name,
  placeholder: defaultPlaceholder = 'Enter your answer...',
  required = false,
  ranges,
  disabled = false,
  type: inputType,
  multiline = false,
}) => {
  const [selectedUnitIndex, setSelectedUnitIndex] = useState(0);
  const [selectedInches, setSelectedInches] = useState<string>('');

  // Helper function to determine if we have multiple unit ranges
  const hasMultipleUnitRanges = React.useMemo(() => {
    if (!Array.isArray(ranges)) return false;
    return ranges.length > 1 && isRange(ranges[0]) && 'unit' in ranges[0];
  }, [ranges]);

  // Get the current range based on selection or single range
  const currentRange = useMemo((): NormalizedRange | null => {
    if (!ranges) return null;

    if (hasMultipleUnitRanges) {
      const range = (ranges as Range[])[selectedUnitIndex];
      return {
        min: range.min,
        max: range.max,
        unit: range.unit,
        precision: range.precision,
        default: 'default' in range ? range.default : undefined,
      };
    }

    if (Array.isArray(ranges)) {
      if (typeof ranges[0] === 'number') {
        return {
          min: ranges[0],
          max: ranges[1] as number,
        };
      }
      if (isRange(ranges[0])) {
        return {
          min: ranges[0].min,
          max: ranges[0].max,
          unit: ranges[0].unit,
          precision: ranges[0].precision,
        };
      }
    }

    if (isRange(ranges)) {
      return {
        min: ranges.min,
        max: ranges.max,
      };
    }

    return null;
  }, [ranges, selectedUnitIndex, hasMultipleUnitRanges]);

  // Check if this is question id 2 with unit "ft" - need inches dropdown
  const isHeightQuestionWithFeet = useMemo(() => {
    return name === '2' && currentRange?.unit === 'ft';
  }, [name, currentRange?.unit]);

  const placeholder = useMemo(() => {
    if (currentRange?.default !== undefined && currentRange.unit) {
      return `e.g. ${currentRange.default}${currentRange.unit}`;
    }
    return defaultPlaceholder;
  }, [currentRange, defaultPlaceholder]);

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control: control as Control<Record<string, unknown>>,
    rules: {
      required: required ? 'This field is required' : false,
      validate: (value: unknown) => {
        // First check if the field is required and empty
        if (required && (!value || value === '')) {
          return 'This field is required';
        }

        // If not required and empty, it's valid
        if (!value || value === '') {
          return true;
        }

        // Get the actual value and unit to validate
        let numValue: number;
        let currentUnit: string | undefined;
        let valueStr: string = '';

        if (isFieldValue(value)) {
          numValue = Number(value.value);
          currentUnit = value.unit;
          valueStr = value.value.toString();
        } else {
          numValue = Number(value);
          valueStr = value.toString();
          currentUnit = hasMultipleUnitRanges
            ? (ranges as Range[])[selectedUnitIndex].unit
            : undefined;
        }

        // Special validation for question id 2 with feet unit - check if inches is selected
        // This needs to be checked after getting the value string
        if (name === '2' && currentUnit === 'ft' && required) {
          // If value is a whole number (no decimal), inches was not selected
          const hasDecimal = valueStr.includes('.');
          if (!hasDecimal) {
            return 'Please select inches';
          }
        }

        // Only validate ranges for number inputs
        if (inputType !== 'number' || !ranges) return true;

        if (isNaN(numValue)) {
          return 'Please enter a valid number';
        }

        // Special validation for feet measurements
        if (currentUnit === 'ft') {
          const [, decimal] = numValue.toString().split('.');
          if (decimal && parseInt(decimal) > 11) {
            return 'For feet measurements, decimals can only go up to .11';
          }
        }

        // Find the correct range based on the unit
        let rangeToValidate: Range;
        if (hasMultipleUnitRanges) {
          if (!currentUnit) {
            return 'Invalid unit';
          }
          const range = (ranges as Range[]).find((r) => r.unit === currentUnit);
          if (!range) {
            return 'Invalid unit';
          }
          rangeToValidate = range;
        } else {
          rangeToValidate = currentRange as Range;
        }

        if (numValue < rangeToValidate.min) {
          return `Value must be at least ${rangeToValidate.min} ${rangeToValidate.unit}`;
        }
        if (numValue > rangeToValidate.max) {
          return `Value must be at most ${rangeToValidate.max} ${rangeToValidate.unit}`;
        }

        return true;
      },
    },
  });

  // Parse decimal value to feet and inches for question id 2
  const parseFeetInches = useMemo(() => {
    // Check if this is question id 2 and unit is "ft" (either from currentRange or field.value)
    const isFtUnit = name === '2' && (
      currentRange?.unit === 'ft' || 
      (isFieldValue(field.value) && (field.value as FieldValue).unit === 'ft')
    );
    
    if (!isFtUnit) return { feet: '', inches: '' };
    
    let numValue: number;
    let isFromAPI = false;
    
    if (isFieldValue(field.value)) {
      const val = (field.value as FieldValue).value;
      // If value is a number, it's from API response (hasn't been processed by handleFeetChange)
      // If value is a string with decimal, user selected inches
      // If value is a string without decimal, user typed feet but hasn't selected inches
      isFromAPI = typeof val === 'number';
      numValue = typeof val === 'string' ? parseFloat(val) : val;
    } else {
      const val = field.value as string;
      numValue = val ? parseFloat(val) : NaN;
    }

    if (isNaN(numValue)) return { feet: '', inches: '' };

    const feet = Math.floor(numValue);
    const decimalPart = numValue - feet;
    
    // If decimalPart is 0 (whole number)
    if (decimalPart === 0) {
      // If it's from API, treat whole number as 0 inches selected (e.g., API returns 5 meaning 5'0")
      if (isFromAPI) {
        return { feet: feet.toString(), inches: '0' };
      }
      
      // Otherwise, check if the string value has a decimal point
      const val = isFieldValue(field.value) ? (field.value as FieldValue).value : field.value;
      const hasDecimal = String(val).includes('.');
      
      if (hasDecimal) {
        // User selected inches (even if 0"), value like "5.00"
        return { feet: feet.toString(), inches: '0' };
      } else {
        // User typed feet but hasn't selected inches yet, value like "5"
        return { feet: feet.toString(), inches: '' };
      }
    }
    
    // Convert decimal to inches (e.g., 0.11 = 11 inches, 0.05 = 5 inches)
    const inches = Math.round(decimalPart * 100);

    return { feet: feet.toString(), inches: inches.toString() };
  }, [name, field.value, currentRange?.unit]);

  // Initialize inches from parsed value
  useEffect(() => {
    // Check if this is question id 2 and unit is "ft" (either from currentRange or field.value)
    const isFtUnit = name === '2' && (
      currentRange?.unit === 'ft' || 
      (isFieldValue(field.value) && (field.value as FieldValue).unit === 'ft')
    );
    
    if (!isFtUnit) {
      // Reset inches if not the height question with feet
      setSelectedInches('');
      return;
    }
    
    const { feet, inches } = parseFeetInches;
    
    // If inches is empty string (no decimal in value), show placeholder
    if (!inches || inches === '') {
      setSelectedInches('');
      return;
    }
    
    // Parse numeric values for comparison
    const feetNum = parseFloat(feet);
    const inchesNum = parseFloat(inches);
    
    // If both feet and inches are 0 (value is "0.00"), show placeholder
    if (!isNaN(feetNum) && !isNaN(inchesNum) && feetNum === 0 && inchesNum === 0) {
      setSelectedInches('');
      return;
    }
    
    // If feet > 0 and inches is 0 (value is "X.00" where X > 0), show "0""
    if (!isNaN(feetNum) && !isNaN(inchesNum) && feetNum > 0 && inchesNum === 0) {
      setSelectedInches('0');
      return;
    }
    
    // Otherwise, show the inches value
    setSelectedInches(inches);
  }, [name, isHeightQuestionWithFeet, parseFeetInches, parseFeetInches.feet, parseFeetInches.inches, field.value, currentRange?.unit]);

  const handleUnitChange = (index: string) => {
    const newIndex = parseInt(index);
    setSelectedUnitIndex(newIndex);

    // Reset inches when changing units (especially when switching away from ft)
    setSelectedInches('');

    // Clear the input value when changing units
    field.onChange({
      value: '',
      unit: (ranges as Range[])[newIndex].unit,
    });
  };

  const formatValueForAPI = (value: string, unit?: string): string => {
    if (!value || value === '' || unit !== 'ft') return value;

    // Only process if it contains a decimal
    if (value.includes('.')) {
      const [whole, decimal] = value.split('.');
      // If decimal is single digit, pad with 0
      if (decimal && decimal.length === 1) {
        return `${whole}.0${decimal}`;
      }
    }
    return value;
  };

  const formatValueForDisplay = (value: string, unit?: string): string => {
    if (!value || value === '' || unit !== 'ft') return value;

    // Only process if it contains a decimal
    if (value.includes('.')) {
      const [whole, decimal] = value.split('.');
      // If decimal has a leading zero, remove it
      if (decimal && decimal.startsWith('0')) {
        return `${whole}.${decimal.substring(1)}`;
      }
    }
    return value;
  };

  // Handle feet input change for question id 2
  const handleFeetChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const feetValue = e.target.value;
    const inches = selectedInches;
    
    // Prevent negative values - don't allow "-" or negative numbers
    if (feetValue.startsWith('-')) {
      return;
    }
    if (feetValue !== '') {
      const numValue = parseFloat(feetValue);
      if (!isNaN(numValue) && numValue < 0) {
        return;
      }
    }
    
    // If inches is not selected, only set feet value (will trigger validation error)
    if (!inches || inches === '') {
      if (feetValue === '' || feetValue === '0') {
        if (hasMultipleUnitRanges) {
          const currentUnit = (ranges as Range[])[selectedUnitIndex].unit;
          field.onChange({
            value: '',
            unit: currentUnit,
          });
        } else {
          field.onChange('');
        }
      } else {
        const feet = parseFloat(feetValue);
        if (isNaN(feet)) return;
        // Store just feet without decimal when inches not selected
        const value = feet.toString();
        if (hasMultipleUnitRanges) {
          const currentUnit = (ranges as Range[])[selectedUnitIndex].unit;
          field.onChange({
            value: value,
            unit: currentUnit,
          });
        } else {
          field.onChange(value);
        }
      }
      return;
    }
    
    // Convert feet and inches to decimal format (e.g., 5 feet 11 inches = 5.11)
    if (feetValue === '' || feetValue === '0') {
      const decimalValue = `0.${inches.padStart(2, '0')}`;
      if (hasMultipleUnitRanges) {
        const currentUnit = (ranges as Range[])[selectedUnitIndex].unit;
        field.onChange({
          value: decimalValue,
          unit: currentUnit,
        });
      } else {
        field.onChange(decimalValue);
      }
      return;
    }

    const feet = parseFloat(feetValue);
    if (isNaN(feet)) return;

    const inchesNum = parseInt(inches) || 0;
    const decimalValue = `${feet}.${inchesNum.toString().padStart(2, '0')}`;

    if (hasMultipleUnitRanges) {
      const currentUnit = (ranges as Range[])[selectedUnitIndex].unit;
      field.onChange({
        value: decimalValue,
        unit: currentUnit,
      });
    } else {
      field.onChange(decimalValue);
    }
  };

  // Handle inches dropdown change for question id 2
  const handleInchesChange = (inches: string) => {
    setSelectedInches(inches);
    const feetValue = parseFeetInches.feet || '';
    
    // If no feet value, handle inches only
    if (!feetValue || feetValue === '') {
      if (inches === '' || inches === '0') {
        if (hasMultipleUnitRanges) {
          const currentUnit = (ranges as Range[])[selectedUnitIndex].unit;
          field.onChange({
            value: '',
            unit: currentUnit,
          });
        } else {
          field.onChange('');
        }
      } else {
        const decimalValue = `0.${inches.padStart(2, '0')}`;
        if (hasMultipleUnitRanges) {
          const currentUnit = (ranges as Range[])[selectedUnitIndex].unit;
          field.onChange({
            value: decimalValue,
            unit: currentUnit,
          });
        } else {
          field.onChange(decimalValue);
        }
      }
      return;
    }

    const feet = parseFloat(feetValue);
    if (isNaN(feet)) return;

    if (inches === '') {
      // If inches is cleared (not selected), just store feet value (will trigger validation error)
      const value = feet.toString();
      if (hasMultipleUnitRanges) {
        const currentUnit = (ranges as Range[])[selectedUnitIndex].unit;
        field.onChange({
          value: value,
          unit: currentUnit,
        });
      } else {
        field.onChange(value);
      }
      return;
    }

    // If inches is '0', store as .00 (0 inches is valid)
    if (inches === '0') {
      const decimalValue = `${feet}.00`;
      if (hasMultipleUnitRanges) {
        const currentUnit = (ranges as Range[])[selectedUnitIndex].unit;
        field.onChange({
          value: decimalValue,
          unit: currentUnit,
        });
      } else {
        field.onChange(decimalValue);
      }
      return;
    }

    const inchesNum = parseInt(inches) || 0;
    const decimalValue = `${feet}.${inchesNum.toString().padStart(2, '0')}`;

    if (hasMultipleUnitRanges) {
      const currentUnit = (ranges as Range[])[selectedUnitIndex].unit;
      field.onChange({
        value: decimalValue,
        unit: currentUnit,
      });
    } else {
      field.onChange(decimalValue);
    }
  };

  const handleValueChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    // If this is question id 2 with feet unit, use separate handler
    if (isHeightQuestionWithFeet) {
      handleFeetChange(e as React.ChangeEvent<HTMLInputElement>);
      return;
    }

    let value = e.target.value;

    // Prevent negative values for number inputs - don't allow "-" or negative numbers
    if (inputType === 'number') {
      if (value.startsWith('-')) {
        return;
      }
      if (value !== '') {
        const numValue = parseFloat(value);
        if (!isNaN(numValue) && numValue < 0) {
          // Don't allow negative values
          return;
        }
      }
    }

    // If it's a number input, and we have precision defined, enforce it
    if (
      inputType === 'number' &&
      currentRange?.precision !== undefined &&
      value !== ''
    ) {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        // Special handling for feet measurements
        if (currentRange.unit === 'ft') {
          const [, decimal] = value.split('.');
          if (decimal && parseInt(decimal) > 11) {
            // If decimal is greater than 11, cap it at 11
            const whole = Math.floor(numValue);
            value = `${whole}.11`;
          }
        } else {
          // Regular precision handling for other units
          const multiplier = 1 / currentRange.precision;
          value = (Math.round(numValue * multiplier) / multiplier).toString();
        }
      }
    }

    if (hasMultipleUnitRanges) {
      const currentUnit = (ranges as Range[])[selectedUnitIndex].unit;
      // Format the value for API only for 'ft' unit
      const formattedValue = formatValueForAPI(value, currentUnit);
      field.onChange({
        value: formattedValue,
        unit: currentUnit,
      });
    } else {
      // Format the value for API only for 'ft' unit in single range
      const formattedValue = formatValueForAPI(value, currentRange?.unit);
      field.onChange(formattedValue);
    }
  };

  // Get the display value for the input
  const displayValue = useMemo(() => {
    // For question id 2 with feet unit, show only feet (inches handled separately)
    if (isHeightQuestionWithFeet) {
      return parseFeetInches.feet;
    }

    let value = '';
    let unit: string | undefined;

    if (isFieldValue(field.value)) {
      value = (field.value as FieldValue).value.toString();
      unit = (field.value as FieldValue).unit;
    } else {
      value = (field.value as string) || '';
      unit = hasMultipleUnitRanges
        ? (ranges as Range[])[selectedUnitIndex].unit
        : currentRange?.unit;
    }
    return formatValueForDisplay(value, unit);
  }, [
    isHeightQuestionWithFeet,
    parseFeetInches.feet,
    field.value,
    selectedUnitIndex,
    hasMultipleUnitRanges,
    ranges,
    currentRange,
  ]);

  // Set initial unit selection based on field value
  useEffect(() => {
    if (
      hasMultipleUnitRanges &&
      isFieldValue(field.value) &&
      (field.value as FieldValue).unit
    ) {
      const unitIndex = (ranges as Range[]).findIndex(
        (range) => range.unit === (field.value as FieldValue).unit,
      );
      if (unitIndex !== -1) {
        setSelectedUnitIndex(unitIndex);
      }
    }
  }, [field.value, ranges, hasMultipleUnitRanges]);

  return (
    <div className="w-full">
      {isHeightQuestionWithFeet && inputType === 'number' && (
        <style>{`
          #input-${name}.hide-number-spinner input[type="number"]::-webkit-inner-spin-button,
          #input-${name}.hide-number-spinner input[type="number"]::-webkit-outer-spin-button,
          .hide-number-spinner input[type="number"]::-webkit-inner-spin-button,
          .hide-number-spinner input[type="number"]::-webkit-outer-spin-button {
            -webkit-appearance: none !important;
            appearance: none !important;
            margin: 0 !important;
            display: none !important;
            opacity: 0 !important;
            pointer-events: none !important;
            height: 0 !important;
            width: 0 !important;
          }
          #input-${name}.hide-number-spinner input[type="number"],
          .hide-number-spinner input[type="number"] {
            -moz-appearance: textfield !important;
            appearance: textfield !important;
          }
        `}</style>
      )}
      <Card className="border-transparent bg-transparent">
        <CardContent className="p-1">
          <div className={cn('flex', (hasMultipleUnitRanges || isHeightQuestionWithFeet) && 'gap-2')}>
            {multiline && inputType === 'text' ? (
              <Textarea
                id={`input-${name}`}
                placeholder={placeholder}
                onChange={handleValueChange}
                onBlur={field.onBlur}
                value={displayValue}
                name={field.name}
                ref={field.ref}
                disabled={disabled}
                className={cn(
                  'min-h-[120px] pb-1 bg-gray-50',
                  isHeightQuestionWithFeet ? 'w-1/3' : hasMultipleUnitRanges ? 'w-1/2' : 'w-full',
                  disabled && 'opacity-50 cursor-not-allowed',
                )}
              />
            ) : (
              <Input
                id={`input-${name}`}
                type={inputType}
                placeholder={placeholder}
                onChange={handleValueChange}
                onBlur={field.onBlur}
                value={displayValue}
                name={field.name}
                ref={field.ref}
                disabled={disabled}
                data-height-input={isHeightQuestionWithFeet && inputType === 'number' ? 'true' : undefined}
                className={cn(
                  'h-14 pb-1',
                  isHeightQuestionWithFeet ? 'w-1/3' : hasMultipleUnitRanges ? 'w-1/2' : 'w-full',
                  disabled && 'opacity-50 cursor-not-allowed',
                  isHeightQuestionWithFeet && inputType === 'number' && 'hide-number-spinner',
                )}
                step={currentRange?.precision || 1}
                min={inputType === 'number' ? (currentRange?.min !== undefined ? Math.max(0, currentRange.min) : 0) : undefined}
              />
            )}
            {isHeightQuestionWithFeet && (
              <Select
                value={selectedInches !== '' ? selectedInches : undefined}
                onValueChange={handleInchesChange}
                disabled={disabled}
              >
                <SelectTrigger className="w-1/3 h-14">
                  <SelectValue
                    placeholder="Inches"
                    className="text-gray-500"
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    key="0"
                    value="0"
                    className="text-gray-500"
                  >
                    0"
                  </SelectItem>
                  {Array.from({ length: 11 }, (_, i) => i + 1).map((inch) => (
                    <SelectItem
                      key={inch}
                      value={inch.toString()}
                      className="text-gray-500"
                    >
                      {inch}"
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {hasMultipleUnitRanges && (
              <Select
                value={selectedUnitIndex.toString()}
                onValueChange={handleUnitChange}
                disabled={disabled}
              >
                <SelectTrigger className={cn('h-14', isHeightQuestionWithFeet ? 'w-1/3' : 'w-1/2')}>
                  <SelectValue
                    placeholder={(ranges as Range[])[0].unit}
                    className="text-gray-500"
                  />
                </SelectTrigger>
                <SelectContent>
                  {(ranges as Range[]).map((range, index) => (
                    <SelectItem
                      key={range.unit}
                      value={index.toString()}
                      className="text-gray-500"
                    >
                      {range.unit}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          {error && <p className="text-red-500 mt-2">{error.message}</p>}
        </CardContent>
      </Card>
    </div>
  );
};

export default InputFieldWrapper;
